// filepath: apps/web/src/features/cabinet/chrome/i18nLocalePreference.ts
//
// FR-UI-CABINET-2 AC-12 / AC-13 — locale persistence.
// FR-I18N-3 — extended locale set to include `uk` and `be`.
//
// The i18n bundle ships five locales (ru / kk / en / uk / be, per
// FR-UI-CORE-8 / FR-I18N-1 / FR-I18N-3). When the user changes the
// locale via the language switcher in the topbar (AC-3), the choice
// is persisted to `localStorage` and survives reload + OIDC re-auth
// (the `onSigninCallback` from ISS-277 does not clear localStorage).
//
// The selection algorithm:
//
//   1. localStorage[KEY] is present and one of the 5 supported locales
//      → use it.
//   2. Otherwise, navigator.language matches one of the 5
//      → use the matched locale.
//   3. Otherwise, fall back to i18next's `fallbackLng` ("ru").
//
// `setDocumentLocale` (apps/web/src/runtime/setDocumentLocale.ts) is
// called on every change so the <html lang> attribute + document
// title stay in sync with the active locale (WCAG 2.4.2 / 3.1.1).
//
// INV-1: localStorage is browser-scoped, not tenant-scoped. A user
// switching tenants in the same browser tab keeps their locale
// preference. This is intentional — locale is a user accessibility
// preference, not a tenant configuration.
// INV-5: this module never stores secrets / tokens; only the locale
// code is persisted.

import { LOCALES, Locale, isLocale } from "@/i18n/locales";

const KEY = "ascoa.locale";

/**
 * Backwards-compatible alias for the consumed-by-react type.
 * The 5-locale set under FR-I18N-3 makes the legacy `SupportedLocale`
 * name semantically equivalent to the shared `Locale` type.
 */
export type SupportedLocale = Locale;

function isSupported(value: string | null): value is SupportedLocale {
  return value !== null && isLocale(value);
}

function readStorage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    // localStorage may throw in private-browsing modes or when the
    // quota is exhausted — fail-closed to the navigator preference.
    return null;
  }
}

function writeStorage(value: SupportedLocale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Silently ignore — locale persistence is best-effort, not
    // blocking. The active in-memory locale still works for this
    // session.
  }
}

function readNavigatorLanguage(): string | null {
  if (typeof navigator === "undefined") return null;
  // navigator.language is the full IETF tag (e.g. "ru-RU", "en-US",
  // "uk-UA", "be-BY"); we only care about the primary subtag.
  const raw = navigator.language;
  if (typeof raw !== "string" || raw.length === 0) return null;
  const primary = raw.split(/[-_]/)[0]?.toLowerCase() ?? "";
  return primary;
}

/**
 * Resolve the locale to apply at boot, applying the preference chain
 * documented above. Pure — does not mutate localStorage or i18n.
 */
export function resolveBootLocale(): SupportedLocale {
  const stored = readStorage();
  if (isSupported(stored)) return stored;
  const nav = readNavigatorLanguage();
  if (isSupported(nav)) return nav;
  return "ru";
}

/**
 * Persist the user's locale choice and notify subscribers.
 * Idempotent — writing the same value is a no-op for subscribers.
 */
export function setLocalePreference(value: SupportedLocale): void {
  writeStorage(value);
  notifyChange();
}

/**
 * Read the currently-stored preference (without resolving the boot
 * chain). Returns null if the user has never made a choice.
 */
export function getStoredLocale(): SupportedLocale | null {
  const stored = readStorage();
  return isSupported(stored) ? stored : null;
}

/**
 * Subscriber registry for the locale preference. The
 * `useLocalePreference` hook (React) subscribes here via
 * `useSyncExternalStore`.
 */
type Listener = () => void;
const listeners = new Set<Listener>();
let snapshot: SupportedLocale | null = getStoredLocale();

function notifyChange(): void {
  snapshot = getStoredLocale();
  for (const l of listeners) l();
}

/**
 * `useSyncExternalStore` plumbing. Exposed as separate functions so
 * the hook can import them directly.
 */
export function subscribeLocalePreference(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getLocaleSnapshot(): SupportedLocale | null {
  return snapshot;
}

export function getServerLocaleSnapshot(): SupportedLocale | null {
  return null;
}

/** Re-export for callers that want the full locale list from this module. */
export { LOCALES };
